<template>
    <div class="af-auth-container">
        <ValidationObserver
            ref="formLogin"
            tag="form"
            @submit.prevent="login()"
        >
            <div class="afu-grid afu-grid-cols md:afu-grid-cols-2 afu-gap-8">
                <div>
                    <ValidationObserver
                        ref="formEmail"
                    >
                        <ValidationProvider
                            v-slot="{ errors }"
                            ref="emailField"
                            name="Email"
                            rules="required|email"
                        >
                            <C1FormInput
                                id="email"
                                v-model="mail"
                                :is-invalid="!!errors[0]"
                                :invalid-feedback="errors[0]"
                                name="email"
                                :placeholder="$t('label.email_address_placeholder')"
                                :label="!hideLabels ? $t('label.email_address') : ''"
                                @keyup.enter="handleRegisterAndApply()"
                            >
                                <template v-slot:label-right>
                                    <C1Spinner
                                        v-if="spinner.check_email"
                                        variant="primary"
                                        small
                                    />
                                    <div
                                        v-if="user && user.email"
                                        class="afu-flex"
                                    >
                                        <div 
                                            v-if="!userCanApply"
                                            class="afu-bg-danger-400 afu-px-2 afu-rounded-btn afu-text-white afu-text-base"
                                        >
                                            <span>{{ $t('apply.application_already_received') }}</span>
                                        </div>
                                        <div
                                            v-else 
                                            class="afu-bg-success-400 afu-px-2 afu-rounded-btn afu-text-white afu-text-base"
                                        >
                                            <span v-if="user.id">{{ $t('login.welcome_back') }}, <b>{{ user.first_name }}</b></span>
                                            <span v-else-if="user.email">{{ $t('login.welcome') }}</span>
                                        </div>
                                    </div>
                                </template>
                            </C1FormInput>
                        </ValidationProvider>
                    </ValidationObserver>
                </div>
                <div v-if="user && user.id && userCanApply">
                    <ValidationProvider
                        v-slot="{ errors }"
                        name="Password"
                        rules="required"
                    >
                        <C1FormInput
                            id="password"
                            v-model="password"
                            :is-invalid="!!errors[0]"
                            :invalid-feedback="errors[0]"
                            type="password"
                            :placeholder="$t('label.password')"
                            :label="!hideLabels ? $t('label.password') : ''"
                        >
                            <template v-slot:label-right>
                                <a
                                    href="#"
                                    class="af-job-apply-forgot-password afu-no-underline"
                                    @click.stop.prevent="sendForgotPasswordCode(user)"
                                >
                                    <C1Spinner
                                        v-if="spinner.forgot_password"
                                        variant="primary"
                                        small
                                    />

                                    <span 
                                        class="afu-text-neutral-dark afu-text-base"
                                    >
                                        {{ $t('label.forgot_password') }}?
                                    </span>
                                </a>
                            </template>
                        </C1FormInput>
                    </ValidationProvider>
                </div>
            </div>
            <div
                v-if="showStep1Buttons()"
                class="afu-my-7 md:afu-my-10 afu-flex md:afu-block"
            >
                <C1Button
                    type="submit"
                    variant="outline-primary"
                    class="afu-mr-4"
                    :disable="password.length <= 0"
                    :is-busy="spinner.login"
                    v-if="!isRegistrationRequired()"
                >
                    {{ $t('label.sign_in_and_apply') }}
                </C1Button>
                <C1Button
                    type="button"
                    variant="outline-primary"
                    class="afu-mr-4"
                    v-if="isRegistrationRequired()"
                    @click.stop.prevent="handleRegisterAndApply()"
                >
                    {{ $t('label.continue_to_apply') }}
                </C1Button>
                <!-- <C1Button
                    type="button"
                    variant="outline-secondary"
                    @click.stop.prevent="handleGuestApply()"
                >
                    {{ $t('label.apply_as_guest') }}
                </C1Button> -->
            </div>
            <div v-if="!!response.message">
                <C1Alert
                    variant="danger"
                >
                    <div><strong>{{ response.message_title }}</strong></div>
                    <span v-html="response.message" />
                </C1Alert>
            </div>
        </ValidationObserver>

        <AuthRegister
            v-if="isRegistering"
            :email="user.email"
            :hide-labels="hideLabels"
            @afterRegister="afterRegister"
        >
            <template slot="fields">
                <slot name="registerFields" />
            </template>
        </AuthRegister>

        <AuthResetPassword
            v-if="isResettingPassword"
            :user="user"
            @backToLogin="changeStateToLogin"
            @handleGuestApply="handleGuestApply"
            @afterLogin="afterLogin"
        />
    </div>
</template>

<script>
    import { mapState, mapActions } from 'vuex';
    import has from 'lodash/has';
    import debounce from 'lodash/debounce';
    import { ValidationObserver, ValidationProvider } from 'vee-validate';
    import C1Button from '@/components/C1Components/C1Button';
    import C1FormInput from '@/components/C1Components/C1FormInput';
    import C1Alert from '@/components/C1Components/C1Alert';
    import C1Spinner from '@/components/C1Components/C1Spinner';
    import AuthMixin from '@/components/Auth/AuthMixin';
    import AuthRegister from '@/components/Auth/AuthRegister';
    import AuthResetPassword from '@/components/Auth/AuthResetPassword';

    export default {
        name: 'Auth',

        components: {
            AuthRegister,
            AuthResetPassword,
            ValidationObserver,
            ValidationProvider,
            C1Button,
            C1FormInput,
            C1Alert,
            C1Spinner,
        },

        mixins: [AuthMixin],

        props: {
            variant: {
                type: String,
                default: 'primary',
            },

            hideLabels: {
                type: Boolean,
                default: false,
            },

            passwordPlaceholder: {
                type: String,
                default: 'Enter your password',
            },

            jobUuid: {
                type: String,
                default: null,
            },
        },

        data() {
            return {
                state: '',
                mail: '',
                user: {},
                password: '',
                response: {
                    message_title: '',
                    message: '',
                },
                spinner: {
                    login: false,
                    forgot_password: false,
                    check_email: false,
                },
                userCanApply: true,
            };
        },

        computed: {
            ...mapState({
                $_apiMessage: state => state.apiMessage,
            }),

            isLogging() {
                return this.state === 'is_logging' && !this.user.id;
            },

            isRegistering() {
                return this.state === 'is_registering' && !this.user.id;
            },

            isResettingPassword() {
                return this.state === 'is_resetting';
            },
        },

        watch: {
            user: {
                handler(user) {
                    if (user.password_expired) {
                        this.changeStateToResetPassword();
                    } else {
                        if (user.email && user.id) {
                            this.changeStateToLogin();
                        } else if (user.email && !user.id) {
                            this.changeStateToRegistrationRequired();
                        } else {
                            this.resetState();
                        }
                    }
                    this.$emit('user', user);
                },
                deep: true,
            },

            mail(value) {
                if (value) {
                    this.validateEmail();
                }
            },
        },

        created() {
            this.mail = this.email;
        },

        methods: {
            ...mapActions('auth', {
                $_forgotPassword: 'forgotPassword',
                $_resetPassword: 'resetPassword',
            }),

            ...mapActions('me', {
                $_checkUserEmail: 'checkEmail',
                $_getJobApplication: 'getJobApplication',
            }),

            async validateEmail() {
                const validator = await this.$refs.formEmail.validate();

                if (validator) {
                    this.checkEmail();
                } else {
                    this.emitEmptyState();
                }
            },

            showStep1Buttons: function() {
                return !!(this.state === '' || this.state === 'registration_required' || this.user);
            },

            isRegistrationRequired() {
                return this.state === 'registration_required' && !this.user.id;
            },

            showRegistrationForm: function() {
                this.state = 'is_registering';
            },

            checkEmail: debounce(function() {
                this.spinner.check_email = true;
                this.$_checkUserEmail({ email: this.mail }).then((response) => {
                    const user = response.data;
                    if (user.password_expired) {
                        this.sendForgotPasswordCode(user);
                    } else {
                        this.spinner.check_email = false;
                        const payload = {
                            seeker_id: user.id,
                        };
                        this.$_getJobApplication({ job_uuid: this.jobUuid, data: payload }).then((response) => {
                            if (response.data.length > 0) {
                                this.userCanApply = this.canUserApply(response.data.length);
                            } else {
                                this.userCanApply = true;
                            }
                            this.onUserChange(user);
                        });                        
                    }
                }).catch(async() => {
                    this.spinner.check_email = false;
                    // Hack to remove user while user types
                    const v = await this.$refs.emailField.validate();
                    if (!v) {
                        this.emitEmptyState();
                    } else {
                        this.onUserChange({ email: this.mail });
                        this.userCanApply = true;
                    }
                });
            }, 300),

            ...mapActions('auth', {
                $_forgotPassword: 'forgotPassword',
            }),

            async login() {
                const validation = await this.$refs.formLogin.validate();
                if (!validation) {
                    return false;
                }

                this.resetResponse();

                const data = {
                    email: this.user.email,
                    password: this.password,
                };

                this.spinner.login = true;
                this.$_login(data).then(() => {
                    this.afterLogin();
                }).catch((error) => {
                    const errorCode = has(error, 'response.data.error') ? error.response.data.error : 'ServerErrorException';
                    this.response.message_title = 'Login failed';
                    this.response.message = this.$_apiMessage[errorCode];
                    this.spinner.login = false;
                });
            },

            sendForgotPasswordCode(user) {
                const data = {
                    email: user.email,
                    code: true,
                };

                this.resetResponse();

                this.spinner.forgot_password = true;
                this.$_forgotPassword(data).then(() => {
                    this.changeStateToResetPassword();
                }).catch((error) => {
                    const errorCode = has(error, 'response.data.error') ? error.response.data.error : 'ServerErrorException';
                    this.response.message_title = 'Forgot password failed';
                    this.response.message = this.$_apiMessage[errorCode];
                }).finally(() => {
                    this.onUserChange(user);
                    this.spinner.check_email = false;
                    this.spinner.forgot_password = false;
                });
            },

            resetResponse() {
                this.response.message_title = '';
                this.response.message = '';
            },

            emitEmptyState() {
                this.onUserChange({});
            },

            onUserChange(user) {
                this.user = user;
            },

            changeStateToResetPassword() {
                this.state = 'is_resetting';
            },

            changeStateToRegistrationRequired() {
                this.state = 'registration_required';
            },

            changeStateToRegister() {
                this.state = 'is_registering';
            },

            changeStateToLogin() {
                this.state = 'is_logging';
            },

            resetState() {
                this.state = '';
            },

            afterLogin() {
                this.$emit('afterLogin');
            },

            afterRegister() {
                this.$emit('afterRegister');
            },

            handleGuestApply() {
                this.$emit('handleGuestApply');
            },

            handleRegisterAndApply() {
                this.$emit('handleApplyAsNewUser');
            },

            userApplicationExists() {
                const payload = {
                    seeker_id: this.user.id,
                };
                this.$_getJobApplication({ job_uuid: this.jobUuid,  data: payload }).then((response) => {
                    if (response.data.length > 0 && window.afConfig?.allow_job_reapply.toLowerCase() === 'no') {
                        this.userCanApply = false;
                    } else {
                        this.userCanApply = true;
                    }
                });
            },

            canUserApply(applicationCount) {
                const allowJobReapply = window?.afConfig?.allow_job_reapply;
                if (!allowJobReapply) {
                    return true;
                }
                const canReapply = allowJobReapply.toLowerCase() === 'yes';
                if (!canReapply) {
                    return false;
                }
                const jobReapplyLimit = window.afConfig?.job_reapply_limit;
                if (!jobReapplyLimit || jobReapplyLimit === '' || applicationCount < jobReapplyLimit) {
                    return true;
                }
                return false;
            },
        },
    };
</script>
